import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppForm } from "../../ElementInspectorV3/elementInspectorForm/form";
import { createPositionSchema } from "../../ElementInspectorV3/utils";
import { DEFAULT_ELEMENT_MULTIPLIER } from "common/constants";
import { DEFAULT_BBOX_SIDE_PDF } from "components/SmartSurveyor/shared/constants";
import { bboxCenter } from "utils/math/bboxCenter";
import type {
  ElementCreationRow,
  UseElementsCreationModeV3Args,
} from "./types";

const newId = () => Date.now().toString();

const DEFAULT_FORM_VALUES: ElementCreationRow[] = [];

export const useElementsCreationModeV3 = ({
  groupOptions,
  creationStep,
  selectedGroupName,
  onCreationStepChange,
  onSelectedGroupNameChange,
  onSaveAll,
  onCancel,
  onElementAdded,
  onElementSelected,
}: UseElementsCreationModeV3Args) => {
  const { t } = useTranslation();

  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isCancelAllModalOpen, setIsCancelAllModalOpen] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        elements: z.array(
          createPositionSchema({
            bbox: t("smartSurveyor.validation.positionRequired"),
            groupId: t("smartSurveyor.validation.elementTypeRequired"),
            multiplier: t("smartSurveyor.validation.multiplierMin"),
          })
            .omit({ note: true })
            .extend({ id: z.string() }),
        ),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: {
      elements: DEFAULT_FORM_VALUES,
    },
    validators: { onBlur: schema, onSubmit: schema },
    onSubmit: async ({ value }) => {
      if (value.elements.length === 0) return;
      setIsSaving(true);
      try {
        await onSaveAll(value.elements);
      } finally {
        setIsSaving(false);
      }
    },
    onSubmitInvalid: () => {
      toast.error(t("smartSurveyor.elementDetails.form.validationFailed"));
    },
  });

  const handleStartAddingElements = useCallback(() => {
    onSelectedGroupNameChange(selectedGroupName.trim());
    form.reset({ elements: [] });
    setActiveIndex(0);
    onCreationStepChange("adding-elements");
  }, [
    form,
    onCreationStepChange,
    selectedGroupName,
    onSelectedGroupNameChange,
  ]);

  const handleCancelAdding = useCallback(() => {
    const hasElements = form.state.values.elements.length > 0;

    if (!hasElements) {
      onCreationStepChange("group-selection");
      return;
    }

    setIsCancelAllModalOpen(true);
  }, [form, onCreationStepChange]);

  const handleBack = useCallback(() => {
    const hasElements = form.state.values.elements.length > 0;

    if (creationStep === "group-selection" || !hasElements) {
      onCancel();
      return;
    }

    setIsDiscardModalOpen(true);
  }, [creationStep, form, onCancel]);

  const handleConfirmDiscard = useCallback(() => {
    setIsDiscardModalOpen(false);
    onCancel();
  }, [onCancel]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const handleConfirmCancelAll = useCallback(() => {
    setIsCancelAllModalOpen(false);
    form.reset({ elements: [] });
    setActiveIndex(0);
    onCreationStepChange("group-selection");
  }, [form, onCreationStepChange]);

  const handleCloseCancelAll = useCallback(() => {
    setIsCancelAllModalOpen(false);
  }, []);

  const removeElement = useCallback(
    (index: number) => {
      const newLength = form.state.values.elements.length - 1;
      form.removeFieldValue("elements", index);
      setActiveIndex((prev) => {
        if (newLength <= 0) return 0;
        if (index === prev && prev > 0) return prev - 1;
        if (prev >= newLength) return Math.max(0, newLength - 1);
        return prev;
      });
      if (newLength <= 0) {
        onCreationStepChange("group-selection");
      }
    },
    [form, onCreationStepChange],
  );

  const addElementAtPosition = useCallback(
    (pos: { x: number; y: number }) => {
      const newIndex = form.state.values.elements.length;
      const halfSide = DEFAULT_BBOX_SIDE_PDF / 2;
      form.pushFieldValue("elements", {
        id: newId(),
        group_id: selectedGroupName,
        bbox: {
          x1: pos.x - halfSide,
          y1: pos.y - halfSide,
          x2: pos.x + halfSide,
          y2: pos.y + halfSide,
        },
        multiplier: DEFAULT_ELEMENT_MULTIPLIER,
      });
      setActiveIndex(newIndex);
      onElementAdded?.(pos);
    },
    [form, selectedGroupName, onElementAdded],
  );

  const selectElement = useCallback(
    (index: number) => {
      setActiveIndex(index);
      const element = form.state.values.elements[index];
      if (element) {
        onElementSelected?.(bboxCenter({ bbox: element.bbox }));
      }
    },
    [form, onElementSelected],
  );

  const handleSaveAll = useCallback(() => {
    form.handleSubmit();
  }, [form]);

  return {
    form,
    activeIndex,
    setActiveIndex,
    selectElement,
    addElementAtPosition,
    removeElement,
    groupOptions,
    isSaving,
    handleSaveAll,
    onCancel,
    creationStep,
    selectedGroupName,
    setSelectedGroupName: onSelectedGroupNameChange,
    handleStartAddingElements,
    handleCancelAdding,
    handleBack,
    isDiscardModalOpen,
    handleConfirmDiscard,
    handleCancelDiscard,
    isCancelAllModalOpen,
    handleConfirmCancelAll,
    handleCloseCancelAll,
  };
};

export type ElementsCreationModeV3Result = ReturnType<
  typeof useElementsCreationModeV3
>;
